// src/ECRANS/Offers.jsx (version simplifiée)
import React, { useState, useEffect } from 'react';
import { Search, MapPin, Phone, Store, User, X, Loader2, Eye, MessageCircle, Plus, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { categories } from '../modules/categories';
import { colorMap } from '../modules/colorMap';
import { useAuth } from '../contexts/AuthContext';
import { useProducts } from '../contexts/ProductsContext';

export default function CrouVenteHome({ isVisitor = false }) {
  const navigate = useNavigate();
  const { userInfo, loading: authLoading } = useAuth();
  const {
    products,
    loadingProducts,
    error,
    selectedProduct,
    setSelectedProduct,
    fetchProducts,
    fetchProductDetails,
    recordView,
  } = useProducts();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Tous');

  // ==================== UTILITAIRES ====================

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';
  };

  const formatPostedDate = (dateString) => {
    if (!dateString) return 'Date inconnue';

    const now = new Date();
    const posted = new Date(dateString);
    const diffMs = now - posted;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      if (diffHours === 0) {
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        return `Il y a ${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`;
      }
      return `Il y a ${diffHours} heure${diffHours > 1 ? 's' : ''}`;
    } else if (diffDays === 1) {
      return 'Il y a 1 jour';
    } else if (diffDays < 7) {
      return `Il y a ${diffDays} jours`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `Il y a ${weeks} semaine${weeks > 1 ? 's' : ''}`;
    } else {
      const months = Math.floor(diffDays / 30);
      return `Il y a ${months} mois`;
    }
  };

  const openWhatsApp = (phone, productName, sellerName) => {
    const cleanPhone = '225' + phone.replace(/\s+/g, '').replace('+', '');
    const nom = sellerName ? sellerName.split(' ')[0] : '';
    const message = `Bonjour*${nom}* , je suis intéressé(e) par *${productName}* que j'ai eu sur Crou Ventes.`;
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${cleanPhone}?text=${encodedMessage}`, '_blank');

    if (selectedProduct) {
      recordView(selectedProduct.id, userInfo);
    }
  };

  const makeCall = (phone) => {
    window.open(`tel:${phone}`, '_self');

    if (selectedProduct) {
      recordView(selectedProduct.id, userInfo);
    }
  };

  const handleProductClick = async (product) => {
    await recordView(product.id, userInfo);

    if (product && product.site) {
      let parsedProduct = { ...product };
      if (parsedProduct.images && typeof parsedProduct.images === 'string') {
        try {
          parsedProduct.images = JSON.parse(parsedProduct.images);
        } catch (e) {
          console.error('[Products] Erreur parsing images:', e);
          parsedProduct.images = [];
        }
      }
      setSelectedProduct(parsedProduct);
    } else {
      fetchProductDetails(product.id);
    }
  };

  // ==================== EFFECTS ====================

  // Charger les produits au montage et quand les filtres changent
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchProducts({
        category: selectedCategory,
        searchQuery: searchQuery,
      });
    }, 500);

    return () => clearTimeout(timer);
  }, [selectedCategory, searchQuery, fetchProducts]);

  // ==================== RENDU ====================

  if (authLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: colorMap.bg.light }}
      >
        <div className="text-center">
          <Loader2
            className="animate-spin mx-auto"
            size={48}
            style={{ color: colorMap.secondary.base }}
          />
          <p className="mt-4" style={{ color: colorMap.text.secondary }}>
            Chargement...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: colorMap.bg.light }}>
      {/* Header */}
      <header
        className="sticky top-0 z-50"
        style={{
          backgroundColor: colorMap.bg.white,
          borderBottom: `1px solid ${colorMap.border.default}`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/logo-image.png" alt="Crou Vente" className="h-14 w-auto" />
            </div>

            <div className="flex items-center gap-4">
              {isVisitor ? (
                <button
                  onClick={() => navigate('/auth')}
                  className="px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center gap-2"
                  style={{
                    backgroundColor: colorMap.secondary.base,
                    color: colorMap.bg.white,
                  }}
                >
                  <Plus size={18} />
                  Vendre
                </button>
              ) : (
                <>
                  {userInfo && (
                    <div
                      className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full"
                      style={{ backgroundColor: colorMap.bg.light }}
                    >
                      <User size={16} style={{ color: colorMap.secondary.base }} />
                      <span
                        className="text-sm font-medium"
                        style={{ color: colorMap.text.dark }}
                      >
                        {userInfo.name || userInfo.phone}
                      </span>
                    </div>
                  )}

                  <button
                    onClick={() => navigate('/posts')}
                    className="px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center gap-2"
                    style={{
                      backgroundColor: colorMap.secondary.base,
                      color: colorMap.bg.white,
                    }}
                  >
                    Vendre un produit
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Search & Filters Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Search Bar */}
          <div className="relative">
            <Search
              className="absolute left-4 top-1/2 transform -translate-y-1/2 pointer-events-none"
              size={20}
              style={{ color: colorMap.text.muted }}
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher un produit, un vendeur..."
              className="w-full pl-12 pr-4 py-4 rounded-xl outline-none border transition-all duration-200"
              style={{
                borderColor: colorMap.border.default,
                backgroundColor: colorMap.bg.white,
              }}
            />
          </div>

          {/* Category Pills */}
          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className="px-4 py-2 rounded-full font-medium whitespace-nowrap transition-all duration-200"
                style={{
                  backgroundColor:
                    selectedCategory === category
                      ? colorMap.primary.base
                      : colorMap.bg.white,
                  color:
                    selectedCategory === category
                      ? colorMap.bg.white
                      : colorMap.text.dark,
                  border: `1px solid ${selectedCategory === category
                      ? colorMap.primary.base
                      : colorMap.border.default
                    }`,
                }}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Results count */}
          <p style={{ color: colorMap.text.secondary }}>
            <span className="font-semibold" style={{ color: colorMap.text.primary }}>
              {loadingProducts ? '...' : products.length}
            </span>{' '}
            produit{products.length > 1 ? 's' : ''} disponible
            {products.length > 1 ? 's' : ''}
            {selectedCategory !== 'Tous' && ` dans "${selectedCategory}"`}
          </p>
        </div>
      </div>

      {/* Products Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {loadingProducts ? (
          <div className="text-center py-16">
            <Loader2
              className="animate-spin mx-auto"
              size={48}
              style={{ color: colorMap.secondary.base }}
            />
            <p className="mt-4" style={{ color: colorMap.text.secondary }}>
              Chargement des produits...
            </p>
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <p style={{ color: colorMap.text.secondary }} className="mb-4">
              {error}
            </p>
            <button
              onClick={() => fetchProducts({ category: selectedCategory, searchQuery })}
              className="px-6 py-2 rounded-lg font-medium"
              style={{
                backgroundColor: colorMap.secondary.base,
                color: colorMap.bg.white,
              }}
            >
              Réessayer
            </button>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-16">
            <h3 className="text-xl font-bold mb-2" style={{ color: colorMap.text.primary }}>
              Aucun produit trouvé
            </h3>
            <p style={{ color: colorMap.text.secondary }}>
              {searchQuery || selectedCategory !== 'Tous'
                ? 'Essayez de modifier votre recherche'
                : 'Aucun produit disponible'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
            {products.map((product) => {
              let images = [];
              if (product.images) {
                try {
                  images =
                    typeof product.images === 'string'
                      ? JSON.parse(product.images)
                      : product.images;
                } catch (e) {
                  console.error('Erreur parsing images:', e);
                }
              }

              return (
                <div
                  key={product.id}
                  className="rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg"
                  style={{
                    backgroundColor: colorMap.bg.card,
                    border: `1px solid ${colorMap.border.default}`,
                  }}
                  onClick={() => handleProductClick(product)}
                >
                  {/* IMAGE */}
                  <div
                    className="relative w-full h-56"
                    style={{ backgroundColor: colorMap.bg.light }}
                  >
                    <img
                      src={
                        images?.length > 0
                          ? images[0]?.thumbnailUrl || images[0]?.url
                          : 'https://via.placeholder.com/600x400?text=Produit'
                      }
                      alt={product.name || 'Produit'}
                      className="w-full h-full object-cover"
                    />

                    <div
                      className="absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-medium"
                      style={{
                        backgroundColor: colorMap.bg.white,
                        color: colorMap.secondary.base,
                      }}
                    >
                      {product.category}
                    </div>
                  </div>

                  {/* CONTENU */}
                  <div className="p-4">
                    <h3
                      className="mb-2 text-lg font-bold line-clamp-2"
                      style={{ color: colorMap.text.primary }}
                    >
                      {product.name}
                    </h3>

                    <p
                      className="mb-3 text-2xl font-bold"
                      style={{ color: colorMap.secondary.base }}
                    >
                      {formatCurrency(product.price)}
                    </p>

                    <div className="mb-3 space-y-2">
                      <div className="flex items-center gap-2">
                        {product.shopName ? (
                          <>
                            <Store size={16} style={{ color: colorMap.text.muted }} />
                            <span className="text-sm" style={{ color: colorMap.text.dark }}>
                              {product.shopName}
                            </span>
                          </>
                        ) : (
                          <>
                            <User size={16} style={{ color: colorMap.text.muted }} />
                            <span className="text-sm" style={{ color: colorMap.text.dark }}>
                              {product.full_name || 'Vendeur'}
                            </span>
                          </>
                        )}
                        {product.is_verified && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold" style={{ backgroundColor: '#dcfce7', color: '#15803d' }}>
                            <ShieldCheck size={12} />
                            Vérifié
                          </span>
                        )}
                      </div>

                      <div className="flex items-start gap-2">
                        <MapPin
                          size={16}
                          className="mt-0.5 flex-shrink-0"
                          style={{ color: colorMap.text.muted }}
                        />
                        <span className="text-xs leading-relaxed" style={{ color: colorMap.text.secondary }}>
                          {`${product.site || ''} - Bat ${product.batiment || ''} - Palier ${product.palier || ''} - Chambre ${product.chambre || ''}`.replace(/^\s-\s/, '')}
                        </span>
                      </div>
                    </div>

                    <div className="mb-3 flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <Eye size={14} style={{ color: colorMap.text.muted }} />
                        <span style={{ color: colorMap.text.secondary }}>
                          {product.views || 0} vue{product.views > 1 ? 's' : ''}
                        </span>
                      </div>
                      <span style={{ color: colorMap.text.muted }}>
                        {formatPostedDate(product.created_at)}
                      </span>
                    </div>

                    {/* BOUTONS */}
                    <div className="flex gap-2">
                      <button
                        className="flex-1 rounded-lg border py-2.5 font-medium"
                        style={{
                          backgroundColor: colorMap.bg.white,
                          color: colorMap.text.dark,
                          borderColor: colorMap.border.default,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          product.phone && makeCall(product.phone);
                        }}
                      >
                        <div className="flex items-center justify-center gap-2">
                          <Phone size={18} />
                          Appel
                        </div>
                      </button>

                      <button
                        className="flex-1 rounded-lg py-2.5 font-medium"
                        style={{
                          backgroundColor: colorMap.primary.base,
                          color: colorMap.bg.white,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          product.phone && openWhatsApp(product.phone, product.name, product.full_name);
                        }}
                      >
                        <div className="flex items-center justify-center gap-2">
                          <MessageCircle size={18} />
                          WhatsApp
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal des détails */}
      {selectedProduct && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
          onClick={() => setSelectedProduct(null)}
        >
          <div
            className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl"
            style={{ backgroundColor: colorMap.bg.white }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Image */}
            <div className="relative w-full h-80 overflow-hidden">
              <img
                src={
                  selectedProduct?.images?.length > 0
                    ? selectedProduct.images[0]?.url
                    : 'https://via.placeholder.com/800x600?text=Produit'
                }
                alt={selectedProduct?.name || 'Produit'}
                className="absolute inset-0 w-full h-full"
                style={{ display: 'block', objectFit: 'cover' }}
              />

              {/* Bouton fermer */}
              <button
                onClick={() => setSelectedProduct(null)}
                className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center transition hover:scale-105"
                style={{
                  backgroundColor: colorMap.bg.white,
                  color: colorMap.text.dark,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                }}
                aria-label="Fermer"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 md:p-8">
              <div className="mb-4">
                <span
                  className="inline-block px-3 py-1 rounded-full text-sm font-medium"
                  style={{
                    backgroundColor: colorMap.secondary.light,
                    color: colorMap.secondary.dark,
                  }}
                >
                  {selectedProduct.category}
                </span>
                <span className="ml-2 text-sm" style={{ color: colorMap.text.muted }}>
                  {formatPostedDate(selectedProduct.created_at)}
                </span>
              </div>

              <h2
                className="text-3xl font-bold mb-4"
                style={{ color: colorMap.text.primary }}
              >
                {selectedProduct.name}
              </h2>

              {selectedProduct.description && (
                <div className="mb-6">
                  <h3
                    className="font-semibold mb-2"
                    style={{ color: colorMap.text.dark }}
                  >
                    Description
                  </h3>
                  <p
                    style={{ color: colorMap.text.secondary }}
                    className="whitespace-pre-line"
                  >
                    {selectedProduct.description}
                  </p>
                </div>
              )}

              <div className="flex items-center gap-4 mb-6 text-sm">
                <div className="flex items-center gap-2">
                  <Eye size={16} style={{ color: colorMap.text.muted }} />
                  <span style={{ color: colorMap.text.secondary }}>
                    {selectedProduct.views || 0} vue
                    {selectedProduct.views > 1 ? 's' : ''}
                  </span>
                </div>
              </div>

              <p
                className="text-4xl font-bold mb-6"
                style={{ color: colorMap.secondary.base }}
              >
                {formatCurrency(selectedProduct.price)}
              </p>

              {/* Informations du vendeur */}
              <div
                className="p-5 rounded-xl mb-6 space-y-4"
                style={{ backgroundColor: colorMap.bg.light }}
              >
                <h3
                  className="font-semibold text-lg mb-3"
                  style={{ color: colorMap.text.primary }}
                >
                  Informations du vendeur
                </h3>

                {selectedProduct.shopName && (
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: colorMap.bg.white }}
                    >
                      <Store
                        size={20}
                        style={{ color: colorMap.secondary.base }}
                      />
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: colorMap.text.muted }}>
                        Boutique
                      </p>
                      <p
                        className="font-semibold"
                        style={{ color: colorMap.text.dark }}
                      >
                        {selectedProduct.shopName}
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: colorMap.bg.white }}
                  >
                    <User
                      size={20}
                      style={{ color: colorMap.secondary.base }}
                    />
                  </div>
                  <div>
                    <p className="text-sm" style={{ color: colorMap.text.muted }}>
                      Vendeur
                    </p>
                    <div className="flex items-center gap-2">
                      <p
                        className="font-semibold"
                        style={{ color: colorMap.text.dark }}
                      >
                        {selectedProduct.full_name || 'Non spécifié'}
                      </p>
                      {selectedProduct.is_verified && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold" style={{ backgroundColor: '#dcfce7', color: '#15803d' }}>
                          <ShieldCheck size={12} />
                          Vérifié
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {(selectedProduct.site ||
                  selectedProduct.batiment ||
                  selectedProduct.chambre) && (
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: colorMap.bg.white }}
                      >
                        <MapPin
                          size={20}
                          style={{ color: colorMap.secondary.base }}
                        />
                      </div>
                      <div>
                        <p className="text-sm" style={{ color: colorMap.text.muted }}>
                          Localisation
                        </p>
                        <p
                          className="font-semibold"
                          style={{ color: colorMap.text.dark }}
                        >
                          {selectedProduct.site && `${selectedProduct.site}`}
                          {selectedProduct.batiment &&
                            ` - Bât: ${selectedProduct.batiment}`}
                          {selectedProduct.palier &&
                            ` - Pal: ${selectedProduct.palier}`}
                          {selectedProduct.chambre &&
                            ` - Ch: ${selectedProduct.chambre}`}
                        </p>
                      </div>
                    </div>
                  )}

                {selectedProduct.phone && (
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: colorMap.bg.white }}
                    >
                      <Phone
                        size={20}
                        style={{ color: colorMap.secondary.base }}
                      />
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: colorMap.text.muted }}>
                        Téléphone
                      </p>
                      <p
                        className="font-semibold"
                        style={{ color: colorMap.text.dark }}
                      >
                        {selectedProduct.phone}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Boutons de contact */}
              {selectedProduct.phone ? (
                <div className="space-y-3">
                  <button
                    className="w-full py-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all duration-300 hover:opacity-90 border"
                    style={{
                      backgroundColor: colorMap.bg.white,
                      color: colorMap.text.dark,
                      boxShadow: '0 4px 12px rgba(231, 235, 242, 0.2)',
                    }}
                    onClick={() =>
                      openWhatsApp(selectedProduct.phone, selectedProduct.name, selectedProduct.full_name)
                    }
                  >
                    <MessageCircle size={20} />
                    Contacter sur WhatsApp
                  </button>

                  <button
                    className="w-full py-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all duration-300 hover:opacity-90"
                    style={{
                      backgroundColor: colorMap.primary.base,
                      color: colorMap.bg.white,
                      boxShadow: '0 4px 12px rgba(34, 197, 94, 0.2)',
                    }}
                    onClick={() => makeCall(selectedProduct.phone)}
                  >
                    <Phone size={20} />
                    Appeler directement
                  </button>

                  <p
                    className="text-center text-sm mt-2"
                    style={{ color: colorMap.text.muted }}
                  >
                    Vous pouvez aussi passer directement à sa chambre
                  </p>
                </div>
              ) : (
                <div
                  className="text-center py-4"
                  style={{ color: colorMap.text.muted }}
                >
                  Numéro de téléphone non disponible
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}